import { format } from "date-fns";
import { useCallback, useRef, useState } from "react";
import GetGenerateClientDocuments from "../api/GetGenerateClientDocuments";
import { sanitizeText } from "../utils/string";

/**
 * Custom React hook that manages the process of generating and downloading client documents as a ZIP file.
 *
 * Handles request lifecycle states (pending, success, error), cancellation of in-progress requests,
 * and automatic cleanup. Can be used to trigger document generation for a specific client by ID and name.
 *
 * @returns {Object} An object containing:
 *   - isPending {boolean} - Indicates whether the generation/download is in progress.
 *   - isSuccess {boolean} - Indicates a successful file download.
 *   - isError {boolean} - Indicates that an error occurred during the process.
 *   - generateDocuments {Function} - Function to trigger the document generation and download.
 *   - reset {Function} - Resets the internal state flags.
 */

const useGenerateClientDocuments = ({ onSuccess, onError, onSettled }) => {
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const controller = useRef(null);

  const generateDocuments = useCallback(
    async ({ clientId, clientName }) => {
      setIsPending(true);
      setIsError(false);
      setIsSuccess(false);

      controller.current?.abort();
      controller.current = new AbortController();

      try {
        const { data } = await GetGenerateClientDocuments({
          id: clientId,
          signal: controller.current.signal,
        });

        const blob = new Blob([data], {
          type: "application/zip",
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;

        a.download = `${sanitizeText(clientName.split(" ").join("_"))}_${format(
          new Date(),
          "yyyy-MM-dd"
        )}.zip`;

        a.click();
        window.URL.revokeObjectURL(url);

        setIsSuccess(true);
        setIsPending(false);
        onSuccess?.();
      } catch (error) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          setIsError(true);
          onError?.();
          return;
        }
        setIsPending(false);
      } finally {
        onSettled?.();
        controller.current = null;
      }
    },
    [onError, onSettled, onSuccess]
  );

  const reset = useCallback(() => {
    controller.current?.abort();
    setIsError(false);
    setIsPending(false);
    setIsSuccess(false);
  }, []);

  return { isPending, isError, isSuccess, generateDocuments, reset };
};

export default useGenerateClientDocuments;
